import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { useAuth } from "@/contexts/AuthContext";
import NotificationCenter from "@/components/notifications/NotificationCenter";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQueryClient } from "@tanstack/react-query";

const languages = [
  { code: "it", label: "IT", fullName: "Italiano" },
  { code: "en", label: "EN", fullName: "English" },
  { code: "es", label: "ES", fullName: "Español" },
];

export const Header = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  // Helper to read cached logos from localStorage
  const getCachedLogos = () => {
    try {
      const cached = localStorage.getItem('platform_logos');
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      // Ignore parsing errors
    }
    return {
      logo: "",
      logoDark: "",
      logoResponsive: "",
      logoResponsiveDark: ""
    };
  };

  const [logos, setLogos] = useState(getCachedLogos);

  // Prefetch professionals data on hover
  const prefetchProfessionals = useCallback(() => {
    // Prefetch active filters (categories/languages)
    queryClient.prefetchQuery({
      queryKey: ['active-filters'],
      queryFn: async () => {
        const { data, error } = await supabase.functions.invoke('get-active-categories');
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 15,
    });

    // Prefetch first page of professionals
    queryClient.prefetchQuery({
      queryKey: ['professionals', 1, '', [], '', [], 'all', 'relevance', undefined, undefined, 50],
      queryFn: async () => {
        const { data, error } = await supabase.functions.invoke('search-professionals', {
          method: 'POST',
          body: { sort: 'relevance', page: 1, limit: 12 },
        });
        if (error) throw error;
        return {
          results: data.results || [],
          total: data.total || 0,
          totalPages: data.totalPages || 0,
          page: data.page || 1,
        };
      },
      staleTime: 1000 * 60 * 5,
    });
  }, [queryClient]);

  useEffect(() => {
    const loadLogos = async () => {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'platform_branding')
        .maybeSingle();

      if (data?.value) {
        const branding = data.value as {
          logo?: string;
          logoDark?: string;
          logoResponsive?: string;
          logoResponsiveDark?: string;
        };
        const newLogos = {
          logo: branding.logo || "",
          logoDark: branding.logoDark || "",
          logoResponsive: branding.logoResponsive || "",
          logoResponsiveDark: branding.logoResponsiveDark || ""
        };
        setLogos(newLogos);
        // Cache logos for instant loading on page navigation
        localStorage.setItem('platform_logos', JSON.stringify(newLogos));
      }
    };

    loadLogos();
  }, []);

  const changeLanguage = async (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("language", lang);
    setLangMenuOpen(false);

    // If user is authenticated, save to preferences and update MailerLite
    if (isAuthenticated) {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Save to user_preferences
          await supabase
            .from("user_preferences")
            .upsert({
              user_id: user.id,
              preferred_language: lang,
            }, {
              onConflict: 'user_id'
            });

          // Update MailerLite with proper await and logging
          try {
            const { data, error } = await supabase.functions.invoke("update-mailerlite-language", {
              body: { language: lang }
            });
            if (error) {
              console.error("MailerLite update error:", error);
            } else {
              console.log("MailerLite language updated:", data);
            }
          } catch (mailerliteErr) {
            console.error("MailerLite update exception:", mailerliteErr);
          }
        }
      } catch (error) {
        console.error("Error saving language preference:", error);
      }
    }
  };

  const navLinks: Array<{ name: string; path?: string; url?: string; external?: boolean }> = [
    { name: t("common.professionals"), path: "/professionals" },
    { name: t("common.events"), path: "/events" },
    { name: t("common.jobs"), path: "/jobs" },
    { name: t("common.pricing"), path: "/pricing" },
    { name: "Mag", url: "https://mag.inclusia.pro/", external: true },
  ];

  return (
    <header className="sticky top-0 z-50 bg-card border-b-[3px] border-dark shadow-[0px_4px_0px_0px] shadow-dark">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            {(isMobile ? logos.logoResponsive : logos.logo) ? (
              <img 
                src={isMobile ? logos.logoResponsive : logos.logo} 
                alt={t("common.appName")}
                className="h-10 object-contain"
              />
            ) : (
              <>
                <div className="w-10 h-10 bg-primary border-[3px] border-dark flex items-center justify-center font-heading font-bold text-xl">
                  I
                </div>
                <span className="font-heading font-bold text-2xl text-dark">
                  {t("common.appName")}
                </span>
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-heading font-semibold text-dark hover:text-primary transition-colors uppercase text-sm tracking-wider"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path!}
                  className="font-heading font-semibold text-dark hover:text-primary transition-colors uppercase text-sm tracking-wider"
                  onMouseEnter={link.path === "/professionals" ? prefetchProfessionals : undefined}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Notification Center */}
            {isAuthenticated && <NotificationCenter />}
            
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 border-[3px] border-dark bg-card hover:bg-surface transition-colors font-heading font-bold text-sm"
              >
                <Globe className="w-4 h-4" />
                {languages.find(l => l.code === i18n.language)?.label || i18n.language.toUpperCase()}
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 bg-card border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark min-w-[140px] z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`block w-full px-4 py-2 text-left font-heading font-semibold text-sm hover:bg-primary transition-colors ${
                        i18n.language === lang.code ? "bg-primary" : ""
                      }`}
                    >
                      {lang.fullName}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <BrutalButton
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard")}
              >
                {t("common.dashboard")}
              </BrutalButton>
            ) : (
              <>
                <BrutalButton
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/auth/login")}
                >
                  {t("common.login")}
                </BrutalButton>
                <BrutalButton size="sm" onClick={() => navigate("/auth/register")}>
                  {t("common.register")}
                </BrutalButton>
              </>
            )}
          </div>

          {/* Mobile Actions (Notification + Dashboard/Login + Menu Button) */}
          <div className="lg:hidden flex items-center gap-2">
            {isAuthenticated && <NotificationCenter />}
            
            {/* Pulsante Dashboard/Login sempre visibile su mobile */}
            {isAuthenticated ? (
              <BrutalButton
                variant="outline"
                size="sm"
                onClick={() => navigate("/dashboard")}
              >
                {t("common.dashboard")}
              </BrutalButton>
            ) : (
              <BrutalButton
                variant="outline"
                size="sm"
                onClick={() => navigate("/auth/login")}
              >
                {t("common.login")}
              </BrutalButton>
            )}
            
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t-[3px] border-dark">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) =>
                link.external ? (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-heading font-semibold text-dark hover:text-primary transition-colors uppercase text-sm tracking-wider"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path!}
                    className="font-heading font-semibold text-dark hover:text-primary transition-colors uppercase text-sm tracking-wider"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                )
              )}
              
              {/* Language Dropdown Mobile */}
              <div className="relative my-2">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 border-[3px] border-dark bg-card hover:bg-surface transition-colors font-heading font-bold text-sm w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {languages.find(l => l.code === i18n.language)?.label || i18n.language.toUpperCase()}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {langMenuOpen && (
                  <div className="mt-2 bg-card border-[3px] border-dark shadow-[4px_4px_0px_0px] shadow-dark">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setMobileMenuOpen(false);
                        }}
                        className={`block w-full px-4 py-2 text-left font-heading font-semibold text-sm hover:bg-primary transition-colors ${
                          i18n.language === lang.code ? "bg-primary" : ""
                        }`}
                      >
                        {lang.fullName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Solo pulsante Registrati per utenti non autenticati */}
              {!isAuthenticated && (
                <BrutalButton
                  size="sm"
                  onClick={() => {
                    navigate("/auth/register");
                    setMobileMenuOpen(false);
                  }}
                >
                  {t("common.register")}
                </BrutalButton>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
